import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { isNil, orderBy, uniqBy } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { filter, withLatestFrom, takeUntil, switchMap, tap, distinctUntilChanged } from 'rxjs/operators';
import { EfficiencyAnalysisConfigDialogComponent } from '@app/efficiency-analysis/dialog/efficiency-analysis-config-dialog/efficiency-analysis-config-dialog.component';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { AuthService } from '@app/shared/services/auth.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { EfficiencyAnalysisColorStatus } from '@app/shared/enums';
import { EfficiencyTresholdService } from './services/efficiency-treshold.service';
import { EfficiencyAnalysisDatastoreService } from './services/efficiency-analysis-datastore.service';
import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { ISODateTimeLastSecondOfDayFormat, ISODateTimeStartOfDayFormat } from '@app/shared/constants';
import { ComparisonValues } from './models/comparison-values';
import { EfficiencyThreshold } from './models/thresholds/efficiency-threshold';
import { IBuildingEfficiencyData } from './models/buildings/building-efficiency-data';
import { BuildingTypes } from '@app/shared/models/building-types';
import { EABuilding } from './models/buildings/efficiency-analysis-buildings';
import { EABuildingDetailsAndChildren } from '@app/shared/models/efficiency-analysis/building-details-children';
import { CheckboxData } from './models/heat-electricity-checkbox';
import { BuildingFlatNode } from './components/building-type-filter/building-type-filter.component';
import { IRawWizardData } from './models/raw-wizard-data';

@Component({
  selector: 'app-efficiency-analysis',
  templateUrl: './efficiency-analysis.component.html',
  styleUrls: ['./efficiency-analysis.component.scss'],
})
export class EfficiencyAnalysisComponent implements OnInit, OnDestroy {
  protected eAComponentUnsubscribe$: Subject<void> = new Subject<void>();

  public electricityColorForm = new FormControl();
  public heatColorForm = new FormControl();
  public objectTypeForm = new FormControl();

  public buildings: EABuilding[] = [];
  public buildingsWithEA: EABuilding[] = [];
  public buildingsWithoutEA: EABuilding[] = [];
  public disableAddObjectButton = false;
  public electricityCheckboxData: CheckboxData[] = [];
  public hasUndefinedBuildings = false;
  public heatCheckboxData: CheckboxData[] = [];
  public isFilterOn = false;
  public maxDate = moment();
  public objectTypeText: string = '';
  public resetBuildingType: Subject<boolean> = new Subject<boolean>();
  public showTree = false;
  public showUndefinedBuildingsBanner: boolean = false;
  public uniqueBuildingsWithEA: EABuilding[] = [];

  private _endDate: Date | string | null = null;
  private _startDate: Date | string | null = null;
  private buildingsToFilter: EABuilding[] = [];
  private buildingTypes: BuildingTypes[] = [];
  private checkedBuildingTypes: EABuilding[] = [];
  private checkedElectricityColor: CheckboxData[] = [];
  private checkedHeatColor: CheckboxData[] = [];
  private comparisonValues: ComparisonValues[] = [];
  private efficientTreshold = 0;
  private filter: EAComponentFilter = {
    hasBuildingType: false,
    hasDate: false,
    hasElectricityColor: false,
    hasHeatColor: false,
  };
  private filter$: Subject<boolean> = new Subject<boolean>();
  private filteredBuildingsWithEA: EABuilding[] = [];
  private inefficientTreshold = 0;
  private isInitialLoad = true;
  private municipalityKey = '';
  private previousCount: number | null = null;
  private undefinedBuildings: EABuilding[] = [];
  private uniqueBuildingsWithoutEA: EABuilding[] = [];

  @Input() set startDate(value) {
    this._startDate = value ? moment(value).format(ISODateTimeStartOfDayFormat) : null;
  }

  @Input() set endDate(value) {
    this._endDate = value ? moment(value).format(ISODateTimeLastSecondOfDayFormat) : null;
  }

  get startDate() {
    return this._startDate;
  }
  get endDate() {
    return this._endDate;
  }

  constructor(
    public authService: AuthService,
    public overlayLoaderService: OverlayLoaderService,
    private _router: Router,
    private buildingStoreService: BuildingsStoreService,
    private efficiencyAnalysisDatastore: EfficiencyAnalysisDatastoreService,
    private efficiencyAnalysisService: EfficiencyAnalysisService,
    private efficiencyTresholdService: EfficiencyTresholdService,
    private matDialog: MatDialog,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) { }

  public ngOnInit(): void {
    this.initializeEfficiencyAnalysis();
  }

  public ngOnDestroy(): void {
    this.eAComponentUnsubscribe$.next();
    this.eAComponentUnsubscribe$.complete();
  }

  public initializeEfficiencyAnalysis(): void {
    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        tap(() => {
          this.overlayLoaderService.ignoreChanges = false;
          this.overlayLoaderService.isLoading = true;
          this.overlayLoaderService.ignoreChanges = true;
        }),
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap(() => this.efficiencyAnalysisDatastore.loadBuildingEfficiencyAnalysis()),
        takeUntil(this.eAComponentUnsubscribe$)
      )
      .subscribe(() => this.initializeBuildings());

    this.efficiencyTresholdService.treshold.subscribe((threshold: EfficiencyThreshold) => {
      this.efficientTreshold = threshold.efficientTreshold;
      this.inefficientTreshold = threshold.inefficientTreshold;
      this.setElectricityCheckboxData();
      this.setHeatCheckboxData();
    });

    this.filter$.pipe(takeUntil(this.eAComponentUnsubscribe$)).subscribe((value: boolean) => {
      this.isFilterOn = value;
      this.initializeBuildings();
    });
  }

  public close(): void {
    this.hasUndefinedBuildings = false;
  }

  public addObject(): void {
    if (this.buildingsWithoutEA.length > 0) {
      this.showTree = !this.showTree;
    } else {
      if (this.undefinedBuildings.length > 0) {
        this.hasUndefinedBuildings = true;
      } else {
        this.matDialog.open(NotificationDialogComponent, {
          data: {
            descriptionText: 'Sie haben bereits für alle möglichen Objekte eine Effizienzanalyse durchgeführt',
            orientation: 'row',
            alignment: 'start',
          },
          panelClass: ['-md'],
        });
      }
    }
  }

  public refreshPage(data: { isProcessDone: boolean; building: IRawWizardData | null }): void {
    if (data.isProcessDone) {
      if (this.isInitialLoad) {
        this.isInitialLoad = false;
      }
      if (this.isFilterOn) {
        this.isFilterOn = false;
      }
      this.resetFilter();
      this.showTree = false;
      this.initializeBuildings(data.building);
    }
  }

  public showConfigDialog(): void {
    this.matDialog
      .open(EfficiencyAnalysisConfigDialogComponent, {
        width: '780px',
        height: '520px',
      })
      .afterClosed()
      .subscribe(() => {
        if (this.efficiencyAnalysisService.isRecalculated) {
          this.refreshPage({ isProcessDone: true, building: null });
          this.efficiencyAnalysisService.isRecalculated = false;
        }
      });
  }

  public clickFilterElement(): void {
    this.showTree = false;
  }

  public storeSelectedColorElectricity(event: CheckboxData): void {
    this.electricityCheckboxData.forEach((el: CheckboxData) => {
      if (el.id === event.id) {
        el.checked = !event.checked;
        if (el.checked) {
          this.checkedElectricityColor.push(el);
        }
        if (!el.checked) {
          const isFound = this.checkedElectricityColor.find((val) => val.id === event.id);
          if (isFound) {
            this.checkedElectricityColor = this.checkedElectricityColor.filter((a) => a.id !== event.id);
          }
        }
      }
    });
  }

  public storeSelectedColorHeat(event: CheckboxData): void {
    this.heatCheckboxData.forEach((el: CheckboxData) => {
      if (el.id === event.id) {
        el.checked = !event.checked;
        if (el.checked) {
          this.checkedHeatColor.push(el);
        }
        if (!el.checked) {
          const isFound = this.checkedHeatColor.find((val) => val.id === event.id);
          if (isFound) {
            this.checkedHeatColor = this.checkedHeatColor.filter((a) => a.id !== event.id);
          }
        }
      }
    });
  }

  public runFilter(): void {
    this.getFilterList();
    this.filteredBuildingsWithEA = [];
    this.previousCount = null;

    const hasFilter = this.filter.hasBuildingType || this.filter.hasDate || this.filter.hasElectricityColor || this.filter.hasHeatColor;

    if (hasFilter) {
      this.filteredBuildingsWithEA = this.getBuildingsToFilter();
      if (this.filter.hasBuildingType) {
        this.filteredBuildingsWithEA = this.filteredBuildingsWithEA.filter(obj1 =>
          this.checkedBuildingTypes.some(obj2 => obj2.guid === obj1.guid)
        );
      }
      if (this.filter.hasDate) {
        this.filteredBuildingsWithEA = this.filterByDate(this.filteredBuildingsWithEA);
      }
      if (this.filter.hasElectricityColor) {
        this.filteredBuildingsWithEA = this.filterByColor(this.checkedElectricityColor, this.filteredBuildingsWithEA, 'electricity');
      }
      if (this.filter.hasHeatColor) {
        this.filteredBuildingsWithEA = this.filterByColor(this.checkedHeatColor, this.filteredBuildingsWithEA, 'heat');
      }
      this.previousCount = this.filteredBuildingsWithEA.length;

      this.filter$.next(true);
    } else if (!hasFilter) {
      this.resetFilter();
    } else if (!isNil(this.previousCount)) {
      this.filter$.next(false);
    }
  }

  public resetFilter(): void {
    this.filter = {
      hasBuildingType: false,
      hasDate: false,
      hasElectricityColor: false,
      hasHeatColor: false,
    };
    this._startDate = null;
    this._endDate = null;
    this.checkedElectricityColor = [];
    this.checkedHeatColor = [];
    this.objectTypeForm.setValue(null);
    this.electricityColorForm.setValue(null);
    this.heatColorForm.setValue(null);
    this.previousCount = null;

    for (const data of this.electricityCheckboxData) {
      data.checked = false;
    }

    for (const data of this.heatCheckboxData) {
      data.checked = false;
    }

    if (this.filteredBuildingsWithEA.length > 0 || (this.filteredBuildingsWithEA.length === 0 && this.isFilterOn)) {
      this.filter$.next(false);
    }

    this.filteredBuildingsWithEA.forEach((building: EABuilding) => this.resetActiveStatus(building));

    this.filteredBuildingsWithEA = [];

    this.resetBuildingType.next(true);
  }

  public showBody(): boolean {
    if (this.uniqueBuildingsWithEA.length > 0) {
      return true;
    } else {
      return this.isFilterOn;
    }
  }

  public toggleBuildingTypeFilter(): void {
    this.showTree = false;
  }

  public onUpdateBuildingWithEa(buildings: BuildingFlatNode[]): void {
    const mappedBuildings: EABuilding[] = buildings.map((flat: BuildingFlatNode) => {
      return this.buildings.find((eaBuilding: EABuilding) => eaBuilding.guid === flat.guid);
    }) as EABuilding[];

    this.checkedBuildingTypes = mappedBuildings.filter((eaBuilding: EABuilding) => !isNil(eaBuilding));

    this.filter.hasBuildingType = this.checkedBuildingTypes.length > 0;

    this.filter.hasBuildingType ? this.objectTypeForm.setValue('objectTypeText') : this.objectTypeForm.setValue(null);
  }

  public onUpdateSelectedText(selectedText: string): void {
    this.objectTypeText = selectedText;
  }

  public gotoDashboard(): void {
    this._router.navigateByUrl('/instrumententafel');
  }

  public goToMap(): void {
    this._router.navigate(['/energiekarte']);
  }

  public updateActiveStatus(building: any): void {
    const buildingTypeId = building.buildingTypeId || building.buildingEfficiencyData.buildingTypeId;
    if (!isNil(building)) {
      const currentState = this.uniqueBuildingsWithEA.some((el: EABuilding) => el.buildingTypeId === buildingTypeId && el.isSelected);

      this.uniqueBuildingsWithEA[this.uniqueBuildingsWithEA.findIndex((el: EABuilding) => el.buildingTypeId === buildingTypeId)].isSelected = !currentState;
      this.showTree = false;
    }
  }

  private initializeBuildings(building?: any): void {
    this.buildingStoreService.selectedBuilding = null;
    this.overlayLoaderService.isLoading = true;
    this.efficiencyAnalysisDatastore.comparisonValues$
      .pipe(takeUntil(this.eAComponentUnsubscribe$))
      .subscribe((val: ComparisonValues[]) => (this.comparisonValues = val));

    this.efficiencyAnalysisDatastore.buildingTypes$
      .pipe(takeUntil(this.eAComponentUnsubscribe$))
      .subscribe((buildingTypes: BuildingTypes[]) => (this.buildingTypes = buildingTypes));

    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        withLatestFrom(this.efficiencyAnalysisDatastore.buildingEfficiencyAnalysis$),
        takeUntil(this.eAComponentUnsubscribe$)
      )
      .subscribe(([municipality, buildings]: [MapStoreMunicipality, IBuildingEfficiencyData[]]) => {
        if (buildings.length > 0) {
          this.buildings = [];
          this.buildingsWithEA = [];
          this.buildingsWithoutEA = [];
          this.undefinedBuildings = [];

          buildings.forEach((building: IBuildingEfficiencyData) => this.populateBuildingsArray(building));

          this.buildings.forEach((building: EABuilding) => {
            building.isSelected = false;
            building.buildingType = this.getBuildingType(building);

            if (
              !isNil(building.electricityColorStatus) &&
              !isNil(building.heatColorStatus) &&
              !isNil(building.efficiencyAnalysisDate) &&
              !isNil(building.electricityComputedValue) &&
              !isNil(building.heatComputedValue)
            ) {
              this.buildingsWithEA.push(building);
            } else {
              this.buildingsWithoutEA.push(building);
            }
          });

          this.executeFilterChecksOnInit(municipality.key);
          this.getUniqueValues();

          this.buildingsWithEA = this.getLatestData(this.buildingsWithEA, this.uniqueBuildingsWithEA);
          if (this.isFilterOn) {
            this.buildingsWithEA = this.buildingsWithEA.filter(obj1 =>
              this.filteredBuildingsWithEA.some(obj2 => obj2.guid === obj1.guid)
            );
            this.uniqueBuildingsWithEA = this.uniqueBuildingsWithEA.filter(obj1 =>
              this.buildingsWithEA.some(obj2 => obj2.buildingTypeId === obj1.buildingTypeId)
            );
          }

          this.mapBuildingsWithoutEA();
          this.mapBuildingsWithEA();

          if (this.filteredBuildingsWithEA.length === 0 && !this.isFilterOn) {
            this.resetBuildingType.next(true);
          }
          this.filteredBuildingsWithEA.forEach((building: EABuilding) => {
            this.resetActiveStatus(building);
          });

          if (!isNil(building)) {
            this.updateActiveStatus(building);
          }
          this.getAddObjectButtonStatus();
          this.showTree = false;
        }
        this.overlayLoaderService.ignoreChanges = false;
        this.overlayLoaderService.isLoading = false;
      });
  }

  private setHeatCheckboxData(): void {
    this.heatCheckboxData = [
      {
        id: 1,
        minThreshold: 0,
        maxThreshold: this.efficientTreshold,
        hexColor: EfficiencyAnalysisColorStatus.efficient,
        name: 'gut',
        checked: false,
      },
      {
        id: 2,
        minThreshold: this.efficientTreshold + 1,
        maxThreshold: this.inefficientTreshold - 1,
        hexColor: EfficiencyAnalysisColorStatus.midEfficient,
        name: 'durchschnittlich',
        checked: false,
      },
      {
        id: 3,
        minThreshold: this.inefficientTreshold,
        maxThreshold: 100,
        hexColor: EfficiencyAnalysisColorStatus.inefficient,
        name: 'schlecht',
        checked: false,
      },
    ];
  }

  private setElectricityCheckboxData(): void {
    this.electricityCheckboxData = [
      {
        id: 1,
        minThreshold: 0,
        maxThreshold: this.efficientTreshold,
        hexColor: EfficiencyAnalysisColorStatus.efficient,
        name: 'gut',
        checked: false,
      },
      {
        id: 2,
        minThreshold: this.efficientTreshold + 1,
        maxThreshold: this.inefficientTreshold - 1,
        hexColor: EfficiencyAnalysisColorStatus.midEfficient,
        name: 'durchschnittlich',
        checked: false,
      },
      {
        id: 3,
        minThreshold: this.inefficientTreshold,
        maxThreshold: 100,
        hexColor: EfficiencyAnalysisColorStatus.inefficient,
        name: 'schlecht',
        checked: false,
      },
    ];
  }

  private executeFilterChecksOnInit(municipalityKey: string): void {
    if (municipalityKey !== this.municipalityKey || this.efficiencyAnalysisService.isRecalculated) {
      this.isInitialLoad = true;
    }

    if (this.isInitialLoad) {
      this.buildingsToFilter = this.buildingsWithEA;
      this.municipalityKey = municipalityKey;
      this.filteredBuildingsWithEA = [];
      this.resetFilter();
      this.isInitialLoad = false;
      this.isFilterOn = false;
    }
  }

  private populateBuildingsArray(building: IBuildingEfficiencyData): void {
    const isInComaprison = this.comparisonValues.find((val: ComparisonValues) => val.buildingTypeId === building.buildingTypeId);

    if (isInComaprison) {
      this.buildings.push(building as EABuilding);
    }

    if (isNil(building.buildingTypeId)) {
      this.undefinedBuildings.push(building as EABuilding);
    }
  }

  private getBuildingType(building: any): any {
    const result = this.buildingTypes.filter((type: BuildingTypes) => type.guid === building.buildingTypeId);
    return result ? result[0] : null;
  }

  private getUniqueValues(): void {
    this.uniqueBuildingsWithEA = uniqBy(this.buildingsWithEA, (building: EABuilding) => building.buildingTypeId);
    this.uniqueBuildingsWithEA = orderBy(this.uniqueBuildingsWithEA, (building: EABuilding) => building.buildingType?.name, ['asc']);
    this.uniqueBuildingsWithoutEA = uniqBy(this.buildingsWithoutEA, (building: EABuilding) => building.buildingTypeId);
    this.uniqueBuildingsWithoutEA = orderBy(this.uniqueBuildingsWithoutEA, (building: EABuilding) => building.buildingType?.name, ['asc']);
  }

  private getLatestData(rawData: EABuilding[], uniqueData: EABuilding[]): EABuilding[] {
    let tempArray: EABuilding[] = [];
    const latestData: EABuilding[] = [];
    uniqueData.forEach((uniq: EABuilding) => {
      tempArray = [];
      this.populateTempArray(uniq, rawData, tempArray);

      let data: any;
      const buildingsPerObjectType: string[] = Array.from(new Set(tempArray.map((item: EABuilding) => item.guid)));
      if (buildingsPerObjectType.length > 1) {
        const uniqueBuildingsPerObjectType: EABuilding[] = [];
        this.populateUniqueBuildingsPerObjectTypeArray(buildingsPerObjectType, tempArray, uniqueBuildingsPerObjectType);
        data = uniqueBuildingsPerObjectType;
      } else {
        data = tempArray.reduce((first: EABuilding, second: EABuilding) => (first.modifyDate > second.modifyDate ? first : second));
      }
      if (data.length > 1) {
        data.forEach((value: any) => latestData.push(value));
      } else {
        latestData.push(data);
      }
    });
    return latestData;
  }

  private populateTempArray(uniq: EABuilding, rawData: EABuilding[], tempArray: EABuilding[]): void {
    rawData.forEach((raw: EABuilding) => {
      if (raw.buildingTypeId === uniq.buildingTypeId) {
        tempArray.push(raw);
      }
    });
  }

  private populateUniqueBuildingsPerObjectTypeArray(buildingsPerObjectType: string[], tempArray: EABuilding[], uniqueBuildingsPerObjectType: EABuilding[]) {
    buildingsPerObjectType.forEach((building: string) => {
      const items: EABuilding[] = tempArray.filter((item: EABuilding) => item.guid.indexOf(building) !== -1);

      if (items.length > 1) {
        const val: EABuilding = items.reduce((first: EABuilding, second: EABuilding) => (first.modifyDate > second.modifyDate ? first : second));
        uniqueBuildingsPerObjectType.push(val);
      } else {
        uniqueBuildingsPerObjectType.push(items[0]);
      }
    });
  }

  private resetActiveStatus(building: EABuilding): void {
    if (this.uniqueBuildingsWithEA.length > 0) {
      this.uniqueBuildingsWithEA[
        this.uniqueBuildingsWithEA.findIndex((eaBuilding: EABuilding) => eaBuilding.buildingTypeId === building.buildingTypeId)
      ].isSelected = false;
    }
    this.showTree = false;
  }

  private mapBuildingsWithoutEA(): void {
    const mappedData: EABuildingDetailsAndChildren[] = this.efficiencyAnalysisService.mapBuildingData(this.buildingsWithoutEA, this.uniqueBuildingsWithoutEA);
    this.efficiencyAnalysisService.buildingsWithoutEA = mappedData;
  }

  private mapBuildingsWithEA(): void {
    const mappedData: EABuildingDetailsAndChildren[] = this.efficiencyAnalysisService.mapBuildingData(this.buildingsWithEA, this.uniqueBuildingsWithEA);
    this.efficiencyAnalysisService.buildingsWithEA = mappedData;
  }

  private filterByDate(array: EABuilding[]): EABuilding[] {
    const buildings: EABuilding[] = [];

    array.forEach((el: EABuilding) => {
      if (isNil(this._startDate) && !isNil(this._endDate) && el.efficiencyAnalysisDate <= this._endDate) {
        buildings.push(el);
      }

      if (!isNil(this._startDate) && isNil(this._endDate) && el.efficiencyAnalysisDate >= this._startDate) {
        buildings.push(el);
      }

      if (!isNil(this._startDate) && !isNil(this._endDate) && el.efficiencyAnalysisDate >= this._startDate && el.efficiencyAnalysisDate <= this._endDate) {
        buildings.push(el);
      }
    });
    return buildings;
  }

  private filterByColor(colorArray: CheckboxData[], arrayToBeSearched: EABuilding[], type: string): EABuilding[] {
    const buildings: EABuilding[] = [];
    colorArray.forEach((el: CheckboxData) => {
      arrayToBeSearched.forEach((building: EABuilding) => {
        if (
          type === 'electricity' &&
          building.efficiencyPercentageElectricity >= el.minThreshold &&
          building.efficiencyPercentageElectricity <= el.maxThreshold
        ) {
          buildings.push(building);
        }

        if (type === 'heat' && building.efficiencyPercentageHeat >= el.minThreshold && building.efficiencyPercentageHeat <= el.maxThreshold) {
          buildings.push(building);
        }
      });
    });
    return buildings;
  }

  private getFilterList(): void {
    this.filter.hasBuildingType = this.checkedBuildingTypes.length > 0;
    this.filter.hasDate = !isNil(this._startDate) || !isNil(this._endDate);
    this.filter.hasElectricityColor = this.checkedElectricityColor.length > 0;
    this.filter.hasHeatColor = this.checkedHeatColor.length > 0;
  }

  private getBuildingsToFilter(): EABuilding[] {
    let buildings: EABuilding[];
    buildings = this.filteredBuildingsWithEA.length > 0 ? this.filteredBuildingsWithEA : this.buildingsToFilter;

    if (this.previousCount === 0) {
      buildings = this.filteredBuildingsWithEA;
    }

    let uniqueBuildings = uniqBy(buildings, (building: EABuilding) => building.buildingTypeId);
    uniqueBuildings = orderBy(uniqueBuildings, (building: EABuilding) => building.buildingType?.name, ['asc']);
    buildings = this.getLatestData(buildings, uniqueBuildings);

    return buildings;
  }

  private getAddObjectButtonStatus(): void {
    this.showUndefinedBuildingsBanner = false;
    this.disableAddObjectButton = false;
    if (this.buildingsWithoutEA.length === 0) {
      if (this.buildingsWithEA.length === 0 && this.undefinedBuildings.length > 0) {
        this.showUndefinedBuildingsBanner = true;
      } else {
        this.showUndefinedBuildingsBanner = false;
        if (this.undefinedBuildings.length === 0) {
          this.disableAddObjectButton = true;
        }
      }
    } else {
      this.showUndefinedBuildingsBanner = false;
    }
  }
}

interface EAComponentFilter {
  hasBuildingType: boolean;
  hasDate: boolean;
  hasElectricityColor: boolean;
  hasHeatColor: boolean;
}
